import { timeParse } from 'd3'
import { isEmpty, some, every } from 'lodash'

import {
  CHANGE_GROUP,
  LOAD_MORE,
  PORTFOLIO_DATA,
  UPDATE_BOND_INVESTORS,
  UPDATE_ESTIMATES,
  UPDATE_EVENTS,
  UPDATE_FUNDAMENTAL_CHARTS,
  UPDATE_HIGH_CONVICTION,
  SET_LOADING,
  UPDATE_PRICES,
  UPDATE_TICKERS_IN_PORTFOLIO,
  UPDATE_TICKERS_IN_UNIVERSAL,
  CURRENT_SCROLL_POSITION,
  SET_BOTTOM,
  SET_COMPANIES,
  CLEAN_UP,
  UPDATE_EVENTS_LOADING,
  UPDATE_PRICE_LOADING,
  UPDATE_FUNDAMENTAL_LOADING,
  CHANGE_SEARCH_INPUT_VALUE,
  CHANGE_RISK_STATUS,
  CHANGE_SECTOR,
  CHANGE_COUNTRY,
  CHANGE_REGION,
  CHANGE_SELECTED_TAB,
  SET_ACTIVE_NODE,
  SET_SHOW_BEHAVIOURAL,
  SET_SHOW_PEER_TOGGLE,
  SET_SHOW_COMPANY_TOGGLE,
  REMOVE_COMPANY,
  SET_ADD_TICKER_REQUEST_PENDING,
  UPDATE_CREDIT_PANEL_LOADING,
  UPDATE_CREDIT_PANEL_DATA,
  UPDATE_BEHAVIOR_CHART_LOADING,
  UPDATE_SHOW_BEHAVIOUR_CHART,
  CHANGE_IMPLIED_RATING,
  CHANGE_SELECTED_RISK_TAB,
  SET_EXISTED_CREDIT_RISK,
  CHANGE_CREDIT_RISK_STATUS,
  UPDATE_CREDIT_DATA_COMPARISON_LOADING,
  UPDATE_CREDIT_DATA_COMPARISON,
  DELETE_CREDIT_DATA_COMPARISON,
  UPDATE_NOTES_LOADING,
  UPDATE_NOTES_DATA,
  GET_WHAT_IF_LOADING,
  GET_WHAT_IF_DATA,
  CALCULATE_WHAT_IF_LOADING,
  CALCULATE_WHAT_IF_DATA,
  CLEAR_CALCULATE_WHAT_IF_DATA,
  UPDATE_PRICES_CURRENCY,
  CHANGE_INDUSTRY,
  SET_LOADING_ADD_DELETE_TICKER,
} from './portfolio.constants'
import { tickersService } from '../../services/tickers.services'
import { Alert } from '../../components/Alert'
import {
  formatBondConvictionChartData,
  formatCreditData,
  formatCreditDataCompare,
  formatEstimateChartData,
  formatEventData,
  formatHighConvictionChartData,
  formatLineChartData,
  formatPriceData,
  formatStackAreaChartData,
  getChartName,
} from '../../ultils/dataUtil'

const TICKER_UNDER_CONSIDERATION_MESSAGE =
  'The request to add this ticker is already under consideration.'
const ADD_TICKER_IMMEDIATE_MESSAGE =
  'This company has now been added to your portfolio.'
const ADD_TICKER_PENDING_MESSAGE =
  'This company has been added to your portfolio, it may take up to 24 hours to appear in your Portfolio List.'

const normalizeAddTickerMessage = (message: any) =>
  typeof message === 'string' ? message.trim() : ''

const isAddTickerRequestPendingResponse = (res: any) =>
  normalizeAddTickerMessage(res?.message) === ADD_TICKER_PENDING_MESSAGE ||
  normalizeAddTickerMessage(res?.error) ===
    TICKER_UNDER_CONSIDERATION_MESSAGE ||
  (!res?.error && !res?.message)

const isAddTickerImmediateResponse = (res: any) =>
  normalizeAddTickerMessage(res?.message) === ADD_TICKER_IMMEDIATE_MESSAGE

const formatTickersInPortfolio = (tickers: any[] = []) => {
  const parseDate = timeParse('%Y-%m-%d')
  let creditRiskDataExisted = false
  const updatedTickers = tickers.map((item: any) => {
    const ticker = {
      ...item,
      date: parseDate(item.date),
    }
    ticker.credit_status && (creditRiskDataExisted = true)
    if (ticker.risk_outlook === 'null') {
      return { ...ticker, risk_outlook: 'Neutral' }
    }
    return ticker
  })

  return {
    creditRiskDataExisted,
    tickers: updatedTickers.filter(
      (d: any) => !d.credit_status || d.credit_status !== 'null',
    ),
  }
}

const dispatchTickersInPortfolio = (dispatch: any, tickers: any[] = []) => {
  const formattedTickers = formatTickersInPortfolio(tickers)
  dispatch({
    type: SET_EXISTED_CREDIT_RISK,
    payload: formattedTickers.creditRiskDataExisted,
  })
  dispatch({
    type: UPDATE_TICKERS_IN_PORTFOLIO,
    payload: formattedTickers.tickers,
  })
}

export const changeSearchInputValue = (dispatch: any) => (value: string) => {
  dispatch({ type: CHANGE_SEARCH_INPUT_VALUE, payload: value })
}

export const changeSelectedTab = (dispatch: any) => (value: string) => {
  dispatch({ type: CHANGE_SELECTED_TAB, payload: value })
}

export const changeSelectedRiskTab = (dispatch: any) => (value: string) => {
  dispatch({ type: CHANGE_SELECTED_RISK_TAB, payload: value })
}

export const changeRiskStatus = (dispatch: any) => (value: string) => {
  dispatch({ type: CHANGE_RISK_STATUS, payload: value })
}

export const changeCreditRiskStatus = (dispatch: any) => (value: string) => {
  dispatch({ type: CHANGE_CREDIT_RISK_STATUS, payload: value })
}

export const changeImpliedRating = (dispatch: any) => (value: string) => {
  dispatch({ type: CHANGE_IMPLIED_RATING, payload: value })
}

export const changeSector = (dispatch: any) => (value: string) => {
  dispatch({ type: CHANGE_SECTOR, payload: value })
}

export const changeIndustry = (dispatch: any) => (value: string) => {
  dispatch({ type: CHANGE_INDUSTRY, payload: value })
}

export const changeCountry = (dispatch: any) => (value: string) => {
  dispatch({ type: CHANGE_COUNTRY, payload: value })
}

export const changeRegion = (dispatch: any) => (value: string) => {
  dispatch({ type: CHANGE_REGION, payload: value })
}

export const changeGroup = (dispatch: any) => (value: string) => {
  dispatch({ type: CHANGE_GROUP, payload: value })
}

export const loadMore = (dispatch: any) => (value: number) => {
  dispatch({ type: LOAD_MORE, payload: value })
}

export const currentScrollPosition = (dispatch: any) => (value: number) => {
  dispatch({ type: CURRENT_SCROLL_POSITION, payload: value })
}

export const setBottom = (dispatch: any) => (value: number) => {
  dispatch({ type: SET_BOTTOM, payload: value })
}

export const setActiveNode = (dispatch: any) => (value: number) => {
  dispatch({ type: SET_ACTIVE_NODE, payload: value })
}

export const getTickersOnlyInPortfolio = (dispatch: any) => async (
  userId: any,
) => {
  dispatch({ type: SET_LOADING, loading: true })
  try {
    const res = await tickersService.getTickersOnlyInPortfolio(userId)
    dispatchTickersInPortfolio(dispatch, res.tickers)
  } catch (e) {
    Alert.info(e.message)
  }
  dispatch({ type: SET_LOADING, loading: false })
}

const getRandomRiskValue = () => {
  const items = ['High', 'Medium', 'Low', 'Neutral']
  const item = items[Math.floor(Math.random() * items.length)]
  return item
}

const getRandomOutputChange = () => {
  const items = [-1, 0, 1]
  const item = items[Math.floor(Math.random() * items.length)]
  return item
}

const getRandomPodValue = () => {
  const items = Array.from(Array(100).keys())
  const item = items[Math.floor(Math.random() * items.length)]
  return item
}

export const getAllTickersInUniverse = (dispatch: any) => async (
  userId: any,
) => {
  const parseDate = timeParse('%Y-%m-%d')
  try {
    const res = await tickersService.getAllTickersInUniverse(userId)
    res.tickers.forEach((item: any) => {
      item.date = parseDate(item.date)
      item.searchTerm = `${item.proper_name} ${item.ticker}`
    })
    const updatedTickers = res.tickers.map((ticker: any) => {
      if (ticker.risk_outlook === 'null') {
        return { ...ticker, risk_outlook: 'Neutral' }
      }
      return ticker
    })
    dispatch({ type: UPDATE_TICKERS_IN_UNIVERSAL, payload: updatedTickers })
  } catch (e) {
    Alert.info(e.message)
  }
}

export const deleteTicker = (dispatch: any) => async (
  userId: any,
  tickerId: any,
) => {
  dispatch({ type: SET_LOADING_ADD_DELETE_TICKER, loading: true })
  try {
    const res = await tickersService.deleteTickers(userId, tickerId)
    dispatch({
      type: REMOVE_COMPANY,
      payload: tickerId,
    })
    res.error && Alert.info(res.error)
    !res.error && Alert.info('Ticker successfully deleted.')
  } catch (e) {
    Alert.info(e.message)
  }
  dispatch({ type: SET_LOADING_ADD_DELETE_TICKER, loading: false })
}

export const getPrices = (dispatch: any) => async (
  tickerId: string,
  fromDate: string,
  toDate: string,
  userId: string,
) => {
  dispatch({ type: UPDATE_PRICE_LOADING, loading: true })
  try {
    const res = await tickersService.getPrices(
      tickerId,
      fromDate,
      toDate,
      userId,
    )
    if (
      res?.error &&
      res?.error ===
        'This company is currently not in your portfolio. Please press Add to Portfolio so it can be monitored for you.'
    ) {
      if (res?.data?.length !== 0) {
        Alert.info(res.error)
      }
    } else if (res?.error) {
      Alert.info(res.error)
    }
    if (res.data) {
      dispatch({ type: UPDATE_PRICES, payload: formatPriceData(res.data) })
      dispatch({ type: UPDATE_PRICES_CURRENCY, payload: res?.currency })
    }
  } catch (e) {
    Alert.info(e.message)
  }
  dispatch({ type: UPDATE_PRICE_LOADING, loading: false })
}

export const getEvents = (dispatch: any) => async (
  tickerId: string,
  userId: string,
) => {
  dispatch({ type: UPDATE_EVENTS_LOADING, loading: true })
  try {
    const res = await tickersService.getEvents(tickerId, userId)
    if (
      res.error &&
      res.error !== 'Sorry, I do not have any results for above search key'
    ) {
      Alert.info(res?.error)
    }
    dispatch({
      type: UPDATE_EVENTS,
      payload: formatEventData(res.data),
    })
  } catch (e) {
    Alert.info(e.message)
  }
  dispatch({ type: UPDATE_EVENTS_LOADING, loading: false })
}

export const getScanCharts = (dispatch: any) => async (
  tickerId: string,
  userId: string,
) => {
  dispatch({ type: UPDATE_FUNDAMENTAL_LOADING, loading: true })
  try {
    const scanChartIds = Array.from(Array(9), (_, index) => index + 1)

    const promises = []
    // eslint-disable-next-line no-restricted-syntax
    for (let chartId of scanChartIds) {
      promises.push(requestFundamentalChart(userId, tickerId, chartId))
    }
    Promise.all(promises)
      .then(result => {
        // hide fundamentals if all charts have no data
        const someChartsExist = result.reduce((acc, curr: any) => {
          return acc || curr.data.length > 1
        }, false)
        // hide fundamentals if one stacked chart is missing
        const stackedChartMissing = result.reduce((acc, curr: any) => {
          return (
            acc || ([1, 4, 7].includes(curr.chartId) && curr.data.length <= 1)
          )
        }, false)
        if (result.length === scanChartIds.length) {
          dispatch({
            type: UPDATE_FUNDAMENTAL_CHARTS,
            payload: {
              result,
              showFundamentals: someChartsExist && !stackedChartMissing,
            },
          })
        }
        dispatch({ type: UPDATE_FUNDAMENTAL_LOADING, loading: false })
      })
      .catch(e => {
        Alert.info(e.message)
        dispatch({ type: UPDATE_FUNDAMENTAL_LOADING, loading: false })
      })
  } catch (e) {
    Alert.info(e.message)
    dispatch({ type: UPDATE_FUNDAMENTAL_LOADING, loading: false })
  }
}

const requestFundamentalChart = (
  userId: string,
  ticketId: string,
  chartId: number,
) => {
  return new Promise((resolve, reject) => {
    tickersService
      .getScanCharts(userId, ticketId, chartId)
      .then(res => {
        resolve({
          chartId,
          chart_type: res.chart_type,
          data: [1, 4, 7].includes(chartId)
            ? formatStackAreaChartData(res.data)
            : formatLineChartData(res.data),
        })
      })
      .catch(err => {
        Alert.info(`Request data for ${getChartName(chartId)} chart fail`)
        resolve({
          chartId,
          data: [],
        })
      })
  })
}

export const getCreditSeries = (dispatch: any) => async (
  userId: string,
  ticketId: string,
  fromDate: string,
  toDate: string,
) => {
  dispatch({ type: UPDATE_CREDIT_PANEL_LOADING, loading: true })
  try {
    const res = await tickersService.getCreditSeries(
      userId,
      ticketId,
      fromDate,
      toDate,
    )
    res.error && Alert.info(res.error)
    // const mockData = creditMock.filter((d: any) => d.ticker === ticketId)
    dispatch({
      type: UPDATE_CREDIT_PANEL_DATA,
      // payload: formatCreditData(mockData, 'date'),
      payload: formatCreditData(res.data, 'date'),
    })
  } catch (e) {
    if (e?.message !== 'Network Error') Alert.info(e.message)
  }
  dispatch({ type: UPDATE_CREDIT_PANEL_LOADING, loading: false })
}

export const getBehaviourAnalysisChartData = (dispatch: any) => async (
  userId: string,
  ticketId: string,
  fromDate: string,
  toDate: string,
) => {
  dispatch({ type: UPDATE_BEHAVIOR_CHART_LOADING, loading: true })
  try {
    const promises = []
    promises.push(getEstimates(userId, ticketId, fromDate, toDate))
    promises.push(getHighConviction(userId, ticketId, fromDate, toDate))
    promises.push(getBondInvestors(userId, ticketId, fromDate, toDate))
    Promise.all(promises)
      .then(result => {
        // if all empty => not showing chart
        // otherwise showing that
        const showBehavioural = result.some(
          item => (item.data?.length ?? 0) > 1,
        )
        dispatch({
          type: UPDATE_SHOW_BEHAVIOUR_CHART,
          loading: showBehavioural,
        })
        result.forEach(res => {
          switch (res.id) {
            case 'ESTIMATE':
              dispatch({
                type: UPDATE_ESTIMATES,
                payload: res.data,
              })
              break
            case 'HIGH_CONVICTION':
              dispatch({
                type: UPDATE_HIGH_CONVICTION,
                payload: res.data,
              })
              break
            case 'BOND_INVESTOR':
              dispatch({
                type: UPDATE_BOND_INVESTORS,
                payload: res.data,
              })
              break
            default:
              break
          }
        })
        dispatch({ type: UPDATE_BEHAVIOR_CHART_LOADING, loading: false })
      })
      .catch(e => {
        dispatch({ type: UPDATE_BEHAVIOR_CHART_LOADING, loading: false })
      })
  } catch (e) {
    Alert.info(e.message)
    dispatch({ type: UPDATE_BEHAVIOR_CHART_LOADING, loading: false })
  }
}

const getBondInvestors = (
  userId: string,
  ticketId: string,
  fromDate: string,
  toDate: string,
): Promise<any> => {
  return new Promise((resolve, reject) => {
    tickersService
      .getBondInvestors(userId, ticketId, fromDate, toDate)
      .then(res => {
        resolve({
          id: 'BOND_INVESTOR',
          data: formatBondConvictionChartData(res.data, 'date'),
        })
      })
      .catch(e => {
        Alert.info(e.message)
        reject(new Error(e.message))
      })
  })
}

const getHighConviction = (
  userId: string,
  ticketId: string,
  fromDate: string,
  toDate: string,
): Promise<any> => {
  return new Promise((resolve, reject) => {
    tickersService
      .getHighConviction(userId, ticketId, fromDate, toDate)
      .then(res => {
        resolve({
          id: 'HIGH_CONVICTION',
          data: formatHighConvictionChartData(res.data, 'date'),
        })
      })
      .catch(e => {
        Alert.info(e.message)
        reject(new Error(e.message))
      })
  })
}

const getEstimates = (
  userId: string,
  ticketId: string,
  fromDate: string,
  toDate: string,
): Promise<any> => {
  return new Promise((resolve, reject) => {
    tickersService
      .getEstimates(userId, ticketId, fromDate, toDate)
      .then(res => {
        resolve({
          id: 'ESTIMATE',
          data: formatEstimateChartData(res.data, 'fiscal_date'),
        })
      })
      .catch(e => {
        Alert.info(e.message)
        reject(new Error(e.message))
      })
  })
}

export const getPortfolioUsers = (dispatch: any) => async (
  clientId: string,
) => {
  dispatch({ type: SET_LOADING, loading: true })
  try {
    const res = await tickersService.getPortfolioUsers(clientId)
    res.error && Alert.info(res.error)
    dispatch({
      type: SET_COMPANIES,
      payload: res.data,
    })
  } catch (e) {
    Alert.info(e.message)
  }
  dispatch({ type: SET_LOADING, loading: false })
}

export const addTickers = (dispatch: any) => async (
  clientId: string,
  userId: string,
) => {
  dispatch({ type: SET_LOADING_ADD_DELETE_TICKER, loading: true })
  try {
    const res = await tickersService.addTickers(clientId, userId)
    const isPendingResponse = isAddTickerRequestPendingResponse(res)
    const shouldRefreshPortfolio =
      isPendingResponse || isAddTickerImmediateResponse(res)
    res.error && Alert.info(res.error)
    if (isPendingResponse) {
      dispatch({ type: SET_ADD_TICKER_REQUEST_PENDING, payload: userId })
    }
    if (shouldRefreshPortfolio) {
      const portfolioRes = await tickersService.getTickersOnlyInPortfolio(
        clientId,
      )
      dispatchTickersInPortfolio(dispatch, portfolioRes.tickers)
    }
    res.message && Alert.info(res.message)
  } catch (e) {
    Alert.info(e.message)
  }
  dispatch({ type: SET_LOADING_ADD_DELETE_TICKER, loading: false })
}

export const setLoading = (dispatch: any) => (value: boolean) => {
  dispatch({ type: SET_LOADING, loading: value })
}

export const updatePortfolioData = (dispatch: any) => (value: any) => {
  dispatch({ type: PORTFOLIO_DATA, payload: value })
}

export const cleanUp = (dispatch: any) => async () => {
  dispatch({ type: CLEAN_UP })
  return Promise.resolve()
}

export const setShowBehavioural = (dispatch: any) => (value: boolean) => {
  dispatch({ type: SET_SHOW_BEHAVIOURAL, payload: value })
}

export const setShowPeerToggle = (dispatch: any) => (value: boolean) => {
  dispatch({ type: SET_SHOW_PEER_TOGGLE, payload: value })
}

export const setShowCompanyToggle = (dispatch: any) => (value: boolean) => {
  dispatch({ type: SET_SHOW_COMPANY_TOGGLE, payload: value })
}

export const getCreditSeriesComparison = (dispatch: any) => async (
  userId: string,
  ticketId: string,
  fromDate: string,
  toDate: string,
) => {
  dispatch({ type: UPDATE_CREDIT_DATA_COMPARISON_LOADING, loading: true })
  try {
    const res = await tickersService.getCreditSeries(
      userId,
      ticketId,
      fromDate,
      toDate,
    )
    dispatch({
      type: UPDATE_CREDIT_DATA_COMPARISON,
      payload: formatCreditDataCompare(res.data, 'date'),
    })
  } catch (e) {
    if (e?.message !== 'Network Error') Alert.info(e.message)
  }
  dispatch({ type: UPDATE_CREDIT_DATA_COMPARISON_LOADING, loading: false })
}

export const deleteItemComparisonState = (dispatch: any) => async (
  itemCompare: any,
) => {
  try {
    dispatch({
      type: DELETE_CREDIT_DATA_COMPARISON,
      payload: { itemDelete: itemCompare },
    })
  } catch (e) {
    if (e?.message !== 'Network Error') Alert.info(e.message)
  }
}

export const getNotes = (dispatch: any) => async (
  tickerId: string,
  userId: string,
) => {
  dispatch({ type: UPDATE_NOTES_LOADING, loading: true })
  try {
    const res = await tickersService.getNotes(tickerId, userId)

    if (res?.error) {
      Alert.info(res.error)
    }
    res.data && res?.data?.length > 0
    dispatch({ type: UPDATE_NOTES_DATA, payload: res.data })
  } catch (e) {
    Alert.info(e.message)
  }
  dispatch({ type: UPDATE_NOTES_LOADING, loading: false })
}

export const getWhatIf = (dispatch: any) => async (
  tickerId: string,
  userId: string,
) => {
  dispatch({ type: GET_WHAT_IF_LOADING, loading: true })
  try {
    const res = await tickersService.getWhatIf(tickerId, userId)
    if (res.data) {
      dispatch({
        type: GET_WHAT_IF_DATA,
        payload: {
          csm: res?.csm,
          bfaiRating: res?.bfai_rating,
          data: res?.data?.length > 0 ? res?.data[0] : {},
          ticker_type: res?.ticker_type,
          date: res?.date,
          ticker: res?.ticker,
        },
      })
    }
  } catch (e) {
    // Alert.info(e.message)
  }
  dispatch({ type: GET_WHAT_IF_LOADING, loading: false })
}

export const calculateWhatIf = (dispatch: any) => async (data: any) => {
  dispatch({ type: CALCULATE_WHAT_IF_LOADING, loading: true })
  try {
    let { type } = data
    delete data.type
    const res = await tickersService.postWhatIfCalculate(data)

    if (res?.error) {
      Alert.info(res.error)
    }
    if (res.data) {
      dispatch({
        type: CALCULATE_WHAT_IF_DATA,
        payload: {
          data: res?.data?.length > 0 ? res?.data[0] : {},
          csmCalculate: res?.csm,
          bfaiRating: res?.bfai_rating,
          typeStage: type,
          payloadOutput: res?.payload ?? {},
        },
      })
    }
  } catch (e) {
    Alert.info(e.message)
  }
  dispatch({ type: CALCULATE_WHAT_IF_LOADING, loading: false })
}

export const clearDataCalculateWhatIf = (dispatch: any) => async () => {
  dispatch({ type: CLEAR_CALCULATE_WHAT_IF_DATA })
}
